import React, { useState } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";

const AccessControlTab = () => {
  const [roles] = useState([
    { id: 1, name: "Administrator", description: "Full system access" },
    { id: 2, name: "Manager", description: "Department management access" },
    { id: 3, name: "Advisor", description: "Client management access" },
    { id: 4, name: "Support", description: "Customer support access" },
  ]);

  const [users] = useState([
    {
      id: 1,
      name: "John Smith",
      role: "Administrator",
      accessLevel: "Full",
      status: "Active",
    },
    {
      id: 2,
      name: "Sarah Johnson",
      role: "Manager",
      accessLevel: "Department",
      status: "Active",
    },
    {
      id: 3,
      name: "Mike Wilson",
      role: "Advisor",
      accessLevel: "Limited",
      status: "Active",
    },
    {
      id: 4,
      name: "Emily Brown",
      role: "Support",
      accessLevel: "Basic",
      status: "Inactive",
    },
  ]);

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Roles Panel */}
      <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Roles</h3>
        </div>
        <div className="p-4">
          <button className="w-full px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-opacity-90 mb-4">
            Add New Role
          </button>
          <div className="space-y-2">
            {roles.map((role) => (
              <div
                key={role.id}
                className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
              >
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{role.name}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-500">{role.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Users Panel */}
      <div className="col-span-8 bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            User Permissions
          </h3>
        </div>
        <div className="p-4">
          <div className="flex justify-between mb-4">
            <input
              type="text"
              placeholder="Search users..."
              className="px-4 py-2 border rounded-lg w-64"
            />
            <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-opacity-90">
              Add User
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Access Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {user.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {user.accessLevel}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          user.status === "Active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                      <button className="text-[#202EC6] hover:text-opacity-80 mr-3">
                        Edit
                      </button>
                      <button className="text-red-600 hover:text-opacity-80">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionManagementTab = () => {
  const [subscriptions] = useState([
    {
      id: 1,
      service: "Portfolio Management Suite",
      plan: "Enterprise",
      cost: "$499/month",
      renewal: "2025-12-31",
      status: "Active",
    },
    {
      id: 2,
      service: "Client Communication Platform",
      plan: "Professional",
      cost: "$299/month",
      renewal: "2025-06-30",
      status: "Active",
    },
    {
      id: 3,
      service: "Market Analysis Tools",
      plan: "Basic",
      cost: "$99/month",
      renewal: "2025-03-31",
      status: "Pending",
    },
  ]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <input
          type="text"
          placeholder="Search subscriptions..."
          className="px-4 py-2 border rounded-lg w-64"
        />
        <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-opacity-90">
          Add Subscription
        </button>
      </div>

      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Service
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Plan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Cost
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Renewal Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {subscriptions.map((subscription) => (
              <tr key={subscription.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {subscription.service}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {subscription.plan}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {subscription.cost}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {new Date(subscription.renewal).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      subscription.status === "Active"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {subscription.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                  <button className="text-[#202EC6] hover:text-opacity-80 mr-3">
                    Edit
                  </button>
                  <button className="text-red-600 hover:text-opacity-80">
                    Cancel
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const IntegrationSettingsTab = () => {
  const [integrations] = useState([
    {
      id: 1,
      name: "CRM System",
      status: "Connected",
      lastSync: "2024-03-15 14:30",
      type: "Essential",
    },
    {
      id: 2,
      name: "Email Marketing",
      status: "Disconnected",
      lastSync: "-",
      type: "Optional",
    },
    {
      id: 3,
      name: "Payment Gateway",
      status: "Connected",
      lastSync: "2024-03-15 12:00",
      type: "Essential",
    },
  ]);

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Integrations List */}
      <div className="col-span-4 bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Available Integrations
          </h3>
        </div>
        <div className="p-4">
          <div className="space-y-2">
            {integrations.map((integration) => (
              <div
                key={integration.id}
                className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
              >
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    {integration.name}
                  </h4>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      integration.status === "Connected"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {integration.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  Type: {integration.type}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Last Sync: {integration.lastSync}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Configuration Panel */}
      <div className="col-span-8 bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Integration Configuration
          </h3>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                API Key
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Enter API key"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Webhook URL
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Enter webhook URL"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sync Frequency
              </label>
              <select className="w-full px-4 py-2 border rounded-lg">
                <option>Every 15 minutes</option>
                <option>Every 30 minutes</option>
                <option>Every hour</option>
                <option>Every 4 hours</option>
                <option>Daily</option>
              </select>
            </div>
            <div className="pt-4">
              <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-opacity-90 mr-2">
                Save Configuration
              </button>
              <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                Test Connection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UpgradeOptionsTab = () => {
  const plans = [
    {
      name: "Basic",
      price: "$99",
      features: [
        "Up to 100 clients",
        "Basic reporting",
        "Email support",
        "5 team members",
        "Standard integrations",
      ],
    },
    {
      name: "Professional",
      price: "$299",
      features: [
        "Up to 500 clients",
        "Advanced reporting",
        "Priority support",
        "15 team members",
        "Advanced integrations",
        "Custom branding",
      ],
    },
    {
      name: "Enterprise",
      price: "$499",
      features: [
        "Unlimited clients",
        "Custom reporting",
        "24/7 support",
        "Unlimited team members",
        "All integrations",
        "White labeling",
        "API access",
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan, index) => (
          <div key={index} className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {plan.name}
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {plan.price}
              <span className="text-sm font-normal text-gray-500 dark:text-gray-500">/month</span>
            </p>
            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center text-gray-700 dark:text-gray-300">
                  <svg
                    className="w-5 h-5 text-green-500 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              className={`w-full px-4 py-2 rounded-lg ${
                index === 1
                  ? "bg-[#029831] text-white"
                  : "border border-gray-300 text-gray-700 dark:text-gray-300"
              } hover:opacity-90`}
            >
              {index === 1 ? "Upgrade Now" : "Select Plan"}
            </button>
          </div>
        ))}
      </div>

      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Custom Plan Builder
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Number of Clients
            </label>
            <input
              type="number"
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="Enter number of clients"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Team Members
            </label>
            <input
              type="number"
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="Enter number of team members"
            />
          </div>
        </div>
        <button className="mt-6 px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:opacity-90">
          Calculate Custom Price
        </button>
      </div>
    </div>
  );
};

const BillingInformationTab = () => {
  const [invoices] = useState([
    { id: 1, date: "2024-03-01", amount: "$499.00", status: "Paid" },
    { id: 2, date: "2024-02-01", amount: "$499.00", status: "Paid" },
    { id: 3, date: "2024-01-01", amount: "$499.00", status: "Paid" },
  ]);

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Payment Methods */}
      <div className="col-span-4 space-y-6">
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Payment Methods
            </h3>
          </div>
          <div className="p-4">
            <button className="w-full px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-opacity-90 mb-4">
              Add Payment Method
            </button>
            <div className="space-y-2">
              <div className="p-3 border rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      •••• •••• •••• 4242
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">Expires 12/25</p>
                  </div>
                  <button className="text-red-600 hover:text-opacity-80">
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Billing Contacts
            </h3>
          </div>
          <div className="p-4">
            <button className="w-full px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-opacity-90 mb-4">
              Add Contact
            </button>
            <div className="space-y-2">
              <div className="p-3 border rounded-lg">
                <p className="font-medium text-gray-900 dark:text-gray-100">John Smith</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">john.smith@example.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice History */}
      <div className="col-span-8 bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Invoice History</h3>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Invoice Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {new Date(invoice.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {invoice.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                      <button className="text-[#202EC6] hover:text-opacity-80">
                        Download PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuditLogsTab = () => {
  const [logs] = useState([
    {
      id: 1,
      timestamp: "2024-03-15 14:30:00",
      user: "John Smith",
      action: "User Login",
      details: "Successful login from 192.168.1.1",
    },
    {
      id: 2,
      timestamp: "2024-03-15 14:25:00",
      user: "Sarah Johnson",
      action: "Permission Change",
      details: "Modified user role from Advisor to Manager",
    },
    {
      id: 3,
      timestamp: "2024-03-15 14:20:00",
      user: "System",
      action: "Backup Created",
      details: "Automated system backup completed",
    },
  ]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <input
            type="text"
            placeholder="Search logs..."
            className="px-4 py-2 border rounded-lg w-64"
          />
          <select className="px-4 py-2 border rounded-lg">
            <option>All Actions</option>
            <option>User Login</option>
            <option>Permission Change</option>
            <option>System Events</option>
          </select>
        </div>
        <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-opacity-90">
          Export Logs
        </button>
      </div>

      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Timestamp
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {log.timestamp}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {log.user}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {log.action}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  {log.details}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FirmSettings = () => {
  const [activeTab, setActiveTab] = useState("access-control");
  const breadcrumbItems = [
    { label: "Administrative", link: "/administrative" },
    { label: "Firm Settings", link: "/administrative/firm-settings" },
  ];

  const tabs = [
    { id: "access-control", label: "Access Control" },
    { id: "subscription-management", label: "Subscription Management" },
    { id: "integration-settings", label: "Integration Settings" },
    { id: "upgrade-options", label: "Upgrade Options" },
    { id: "billing-information", label: "Billing Information" },
    { id: "audit-logs", label: "Audit Logs" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "access-control":
        return <AccessControlTab />;
      case "subscription-management":
        return <SubscriptionManagementTab />;
      case "integration-settings":
        return <IntegrationSettingsTab />;
      case "upgrade-options":
        return <UpgradeOptionsTab />;
      case "billing-information":
        return <BillingInformationTab />;
      case "audit-logs":
        return <AuditLogsTab />;
      default:
        return null;
    }
  };

  return (
    <SubPageLayout
      title="Firm Settings"
      subtitle="Configure and customize your firm's system settings"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="flex justify-end">
          <div className="relative w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search settings..."
              className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#029831] focus:border-[#029831]"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-[#029831] text-[#029831]"
                    : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </SubPageLayout>
  );
};

export default FirmSettings;
